import { useEffect, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDbState, subscribeDbState, type DbState } from './session';
import type { Bind, Row } from './protocol';
import type { DbClient } from './client';

export function useDbState(): DbState {
  return useSyncExternalStore(subscribeDbState, getDbState);
}

/** The open database. Only call below <DbGate>, which renders children once the DB is ready. */
export function useDb(): DbClient {
  const state = useDbState();
  if (state.status !== 'ready') throw new Error('useDb() called before the database is ready.');
  return state.client;
}

/**
 * Read query that refetches whenever one of `tables` is written.
 * `tables` must list every table the SQL reads from.
 */
export function useDbQuery<T extends object = Row>(sql: string, bind: Bind = [], tables: string[]) {
  const client = useDb();
  return useQuery({
    queryKey: ['db', sql, bind],
    queryFn: () => client.query<T>(sql, bind),
    meta: { tables }
  });
}

/** Invalidates React Query caches for tables the worker reports as changed. */
export function useTableInvalidation(client: DbClient) {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      client.onTablesChanged((tables) => {
        void queryClient.invalidateQueries({
          predicate: (query) => {
            const queryTables = query.meta?.tables as string[] | undefined;
            return !!queryTables?.some((table) => tables.includes(table));
          }
        });
      }),
    [client, queryClient]
  );
}
